/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Wireless clients on floor map — AP-associated placement, RSSI, roaming, speed test metadata.
 */

import {
  demoClientIp,
  demoConnectedSince,
  generateRoamingHistory,
} from './clientTelemetry'
import { distanceToNearestWall } from './hitTest'
import { resolveObstacleFootprint } from './obstacles'
import { rssiFromApAtPoint } from './rfSimulation'
import type { FloorClientDot, FloorGeometry, FloorObstacle, FloorRoom, PlacedAp } from './types'

const CLIENT_LABELS = ['Laptop', 'Phone', 'Tablet', 'IoT', 'Camera', 'POS'] as const
const DEVICE_TYPES = ['Windows Laptop', 'iPhone', 'Android Phone', 'iPad', 'MacBook', 'IoT Sensor'] as const

const ROOM_MARGIN_PCT = 1.2
const MIN_CLIENT_SEPARATION_M = 1.1
const MIN_WALL_CLEARANCE_M = 0.7
const MAX_PLACEMENT_ATTEMPTS = 28

function clampPct(v: number) {
  return Math.max(2, Math.min(98, v))
}

function hashStr(s: string) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return h
}

function hash01(seed: string) {
  return ((hashStr(seed) >>> 0) % 10_000) / 10_000
}

function bandLabel(bandGHz: number) {
  if (bandGHz >= 6) return '6GHz'
  if (bandGHz >= 5) return '5GHz'
  return '2.4GHz'
}

function distM(x1: number, y1: number, x2: number, y2: number, widthM: number, heightM: number) {
  return Math.hypot(((x2 - x1) / 100) * widthM, ((y2 - y1) / 100) * heightM)
}

function pointInRoom(x: number, y: number, room: FloorRoom, insetPct = 0) {
  return x >= room.x + insetPct && x <= room.x + room.w - insetPct && y >= room.y + insetPct && y <= room.y + room.h - insetPct
}

function pointInObstacle(x: number, y: number, o: FloorObstacle, widthM: number, heightM: number) {
  const { w, h } = resolveObstacleFootprint(o, widthM, heightM)
  const rx = o.x - w / 2
  const ry = o.y - h / 2
  return x >= rx && x <= rx + w && y >= ry && y <= ry + h
}

function findRoomForAp(ap: PlacedAp, rooms: FloorRoom[]) {
  for (const room of rooms) {
    if (pointInRoom(ap.x, ap.y, room, 0)) return room
  }
  let best: FloorRoom | null = null
  let bestD = Infinity
  for (const room of rooms) {
    const cx = room.x + room.w / 2
    const cy = room.y + room.h / 2
    const d = Math.hypot(ap.x - cx, ap.y - cy)
    if (d < bestD) {
      bestD = d
      best = room
    }
  }
  return best
}

function isStickyClient(
  servingAp: PlacedAp,
  x: number,
  y: number,
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
): boolean {
  const servingRssi = rssiFromApAtPoint(servingAp, x, y, geometry, widthM, heightM)
  let secondBest = -999
  for (const ap of aps) {
    if (ap.ap === servingAp.ap) continue
    secondBest = Math.max(secondBest, rssiFromApAtPoint(ap, x, y, geometry, widthM, heightM))
  }
  return secondBest > -78 && servingRssi - secondBest < 6
}

function tooCloseToExisting(x: number, y: number, existing: { x: number; y: number }[], widthM: number, heightM: number) {
  return existing.some(p => distM(x, y, p.x, p.y, widthM, heightM) < MIN_CLIENT_SEPARATION_M)
}

function isValidClientPosition(
  x: number,
  y: number,
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
  existing: { x: number; y: number }[],
  room?: FloorRoom,
) {
  if (room && !pointInRoom(x, y, room, ROOM_MARGIN_PCT)) return false
  const wallDistPct = distanceToNearestWall(x, y, geometry.walls)
  const wallDistM = (wallDistPct / 100) * Math.min(widthM, heightM)
  if (geometry.walls.length > 0 && wallDistM < MIN_WALL_CLEARANCE_M) return false
  for (const o of geometry.obstacles) {
    if (pointInObstacle(x, y, o, widthM, heightM)) return false
  }
  if (tooCloseToExisting(x, y, existing, widthM, heightM)) return false
  return true
}

function samplePointInRoom(
  room: FloorRoom,
  seed: string,
  widthM: number,
  heightM: number,
  geometry: FloorGeometry,
  existing: { x: number; y: number }[],
): { x: number; y: number } | null {
  const innerW = Math.max(0.5, room.w - ROOM_MARGIN_PCT * 2)
  const innerH = Math.max(0.5, room.h - ROOM_MARGIN_PCT * 2)
  if (innerW <= 0 || innerH <= 0) return null

  for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
    const u = hash01(`${seed}:u:${attempt}`)
    const v = hash01(`${seed}:v:${attempt}`)
    const x = clampPct(room.x + ROOM_MARGIN_PCT + u * innerW)
    const y = clampPct(room.y + ROOM_MARGIN_PCT + v * innerH)
    if (isValidClientPosition(x, y, geometry, widthM, heightM, existing, room)) {
      return { x, y }
    }
  }
  const cx = clampPct(room.x + room.w / 2)
  const cy = clampPct(room.y + room.h / 2)
  if (isValidClientPosition(cx, cy, geometry, widthM, heightM, existing, room)) {
    return { x: cx, y: cy }
  }
  return null
}

function samplePointNearAp(
  ap: PlacedAp,
  seed: string,
  widthM: number,
  heightM: number,
  geometry: FloorGeometry,
  existing: { x: number; y: number }[],
  room?: FloorRoom | null,
): { x: number; y: number } | null {
  if (room) {
    return samplePointInRoom(room, seed, widthM, heightM, geometry, existing)
  }

  const minM = 1.2
  const maxM = Math.min(10, Math.min(widthM, heightM) * 0.18)
  for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt++) {
    const angle = hash01(`${seed}:a:${attempt}`) * Math.PI * 2
    const t = hash01(`${seed}:r:${attempt}`)
    const dist = minM + t * (maxM - minM)
    const x = clampPct(ap.x + ((Math.cos(angle) * dist) / widthM) * 100)
    const y = clampPct(ap.y + ((Math.sin(angle) * dist) / heightM) * 100)
    if (isValidClientPosition(x, y, geometry, widthM, heightM, existing)) {
      return { x, y }
    }
  }
  return null
}

/** Demo clients associated to each AP — placed in AP room or near AP (estimated map position). */
export function buildFloorClientOverlay(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
): FloorClientDot[] {
  if (aps.length === 0) return []

  const ssid = 'VVDN-Demo'
  const band = bandLabel(geometry.bandGHz)
  const rooms = geometry.rooms.filter(r => r.w > 0.5 && r.h > 0.5)
  const placed: { x: number; y: number }[] = []
  const dots: FloorClientDot[] = []

  for (const ap of aps) {
    const baseHash = hashStr(ap.ap)
    const count = 2 + (Math.abs(baseHash) % 5)
    const room = rooms.length > 0 ? findRoomForAp(ap, rooms) : null

    for (let i = 0; i < count; i++) {
      const seed = `${ap.ap}:${i}`
      const pt = samplePointNearAp(ap, seed, widthM, heightM, geometry, placed, room)
      if (!pt) continue

      const h = hashStr(`${seed}:meta`)
      const clientMac = `demo-${(h >>> 0).toString(16).padStart(8, '0').slice(0, 8)}`
      const label = `${CLIENT_LABELS[Math.abs(h) % CLIENT_LABELS.length]}-${(Math.abs(h) % 90) + 10}`
      const rssi = Math.round(rssiFromApAtPoint(ap, pt.x, pt.y, geometry, widthM, heightM))
      const sticky = isStickyClient(ap, pt.x, pt.y, aps, geometry, widthM, heightM)

      dots.push({
        id: clientMac,
        mac: clientMac,
        x: pt.x,
        y: pt.y,
        ap: ap.ap,
        rssi,
        ssid,
        band,
        label,
        sticky,
        deviceType: DEVICE_TYPES[Math.abs(h >> 3) % DEVICE_TYPES.length],
        ipAddress: demoClientIp(clientMac),
        connectedSince: demoConnectedSince(clientMac),
        locationEstimated: true,
        roamingHistory: generateRoamingHistory(clientMac, ap.ap, aps, rssi),
        lastSpeedTest: null,
      })
      placed.push(pt)
    }
  }

  return dots
}

export function mergeApClientCounts(aps: PlacedAp[], clients: FloorClientDot[]): PlacedAp[] {
  const counts = new Map<string, number>()
  for (const c of clients) counts.set(c.ap, (counts.get(c.ap) ?? 0) + 1)
  return aps.map(ap => ({ ...ap, clients: counts.get(ap.ap) ?? 0 }))
}

export function computeClientCapacity(clients: FloorClientDot[], apCount: number) {
  const associatedClients = clients.length
  const recommendedMax = Math.max(1, apCount * 25)
  const utilizationPct = Math.min(100, Math.round((associatedClients / recommendedMax) * 100))
  return { clients: associatedClients, max: recommendedMax, pct: utilizationPct }
}

export function syncFloorClients(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  widthM: number,
  heightM: number,
) {
  const clients = buildFloorClientOverlay(aps, geometry, widthM, heightM)
  return {
    clients,
    apsWithCounts: mergeApClientCounts(aps, clients),
    capacity: computeClientCapacity(clients, aps.length),
  }
}

export function updateClientSpeedTest(
  clients: FloorClientDot[],
  clientId: string,
  result: FloorClientDot['lastSpeedTest'],
): FloorClientDot[] {
  return clients.map(c => (c.id === clientId ? { ...c, lastSpeedTest: result ?? null } : c))
}

export function rssiClientColor(rssi: number, sticky = false) {
  if (sticky) return '#f59e0b'
  if (rssi >= -65) return '#10b981'
  if (rssi >= -75) return '#eab308'
  return '#ef4444'
}

export function formatClientRssi(rssi: number) {
  return `${rssi} dBm`
}
